import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PawPrint, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Public landing for a puppy invite link. Two paths:
 *   1) Visitor not logged in → show signup form pre-filled with invite data.
 *   2) Visitor logged in     → show a one-click "Claim" button.
 * Either path ends with the invite claimed and the user redirected to /overview.
 */
export default function PuppyInvitePage() {
  const { token } = useParams();
  const { user, register, login } = useAuth();
  const nav = useNavigate();

  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [mode, setMode] = useState("register"); // "register" | "login"
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/puppy-invites/by-token/${token}`);
        setInvite(data);
        setForm((f) => ({
          ...f,
          name: data.owner_name || "",
          email: data.owner_email || "",
        }));
      } catch (err) {
        setLoadError(err?.response?.data?.detail || "Invite not found or expired.");
      }
    })();
  }, [token]);

  // Auto-claim immediately when a logged-in user lands on this page with a
  // matching email — saves them a click.
  useEffect(() => {
    if (!user || !invite || claiming || invite.status !== "pending") return;
    if ((user.email || "").toLowerCase() !== (invite.owner_email || "").toLowerCase()) return;
    doClaim();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, invite]);

  const doClaim = async () => {
    setClaiming(true);
    try {
      await api.post(`/puppy-invites/${token}/claim`, {});
      toast.success(`${invite?.puppy_name || "Your puppy"} has been added to your account!`);
      nav("/overview");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not claim invite.");
    } finally {
      setClaiming(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    let ok = false;
    if (mode === "register") {
      ok = await register(form.email, form.password, form.name, false);
    } else {
      ok = await login(form.email, form.password);
    }
    setSubmitting(false);
    // Claim runs in the useEffect once `user` is populated, no need to call here.
    if (!ok) {
      // Auth errors are surfaced via the AuthContext error toast/banner; nothing else to do.
    }
  };

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="invite-error">
        <Card className="max-w-md w-full border-border shadow-none">
          <CardContent className="p-8 text-center">
            <p className="text-sm text-destructive font-medium">{loadError}</p>
            <Link to="/" className="block mt-4 text-xs text-muted-foreground underline">← Back home</Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invite) {
    return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">Loading…</div>;
  }

  if (invite.status === "claimed") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="max-w-md w-full border-border shadow-none">
          <CardContent className="p-8 text-center" data-testid="invite-claimed">
            <Check className="mx-auto text-primary mb-3" size={36} />
            <p className="font-medium">This invite has already been claimed.</p>
            <Link to="/login" className="block mt-4 text-sm text-primary underline">Sign in to your account →</Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6" data-testid="invite-page">
      <div className="max-w-2xl mx-auto pt-10 sm:pt-20">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
            <PawPrint size={18} />
          </div>
          <span className="font-display font-bold text-lg">Your Dog Days</span>
        </div>

        <p className="text-xs uppercase tracking-[0.22em] text-primary font-semibold mb-2">A puppy is waiting for you</p>
        <h1 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tighter mb-4">
          {invite.breeder_name} has invited you to claim <span className="text-primary">{invite.puppy_name}</span>.
        </h1>
        <p className="text-base text-muted-foreground mb-8 leading-relaxed">
          Create your free Your Dog Days account to receive a copy of {invite.puppy_name}&apos;s records. {invite.breeder_name} keeps their original copy — you get yours.
        </p>

        {invite.message && (
          <Card className="border-l-4 border-l-[#CC9C33] bg-secondary shadow-none mb-8">
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Note from {invite.breeder_name}</p>
              <p className="text-sm italic" data-testid="invite-message">{invite.message}</p>
            </CardContent>
          </Card>
        )}

        <Card className="border-border shadow-none">
          <CardContent className="p-6 sm:p-8">
            {user ? (
              <div data-testid="invite-claim-card">
                <p className="text-sm text-muted-foreground mb-4">
                  You&apos;re signed in as <strong className="text-foreground">{user.email}</strong>.
                </p>
                <Button onClick={doClaim} disabled={claiming} className="bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto" data-testid="invite-claim-button">
                  {claiming ? "Claiming…" : `Add ${invite.puppy_name} to my account →`}
                </Button>
              </div>
            ) : (
              <>
                <div className="flex gap-1 mb-5 p-0.5 bg-muted rounded-md w-fit">
                  <button
                    type="button"
                    onClick={() => setMode("register")}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${mode === "register" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid="invite-tab-register"
                  >
                    Create account
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("login")}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${mode === "login" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid="invite-tab-login"
                  >
                    I already have one
                  </button>
                </div>

                <form onSubmit={submit} className="space-y-4" data-testid="invite-auth-form">
                  {mode === "register" && (
                    <div>
                      <Label htmlFor="inv-name">Your name</Label>
                      <Input id="inv-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" data-testid="invite-register-name" />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="inv-email">Email</Label>
                    <Input id="inv-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5" data-testid={mode === "register" ? "invite-register-email" : "invite-login-email"} />
                  </div>
                  <div>
                    <Label htmlFor="inv-pass">Password</Label>
                    <Input id="inv-pass" type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="mt-1.5" data-testid={mode === "register" ? "invite-register-password" : "invite-login-password"} />
                  </div>
                  <Button type="submit" disabled={submitting} className="bg-primary text-primary-foreground hover:bg-primary/90 w-full" data-testid={mode === "register" ? "invite-register-submit" : "invite-login-submit"}>
                    {submitting ? "Working…" : mode === "register" ? `Create account & claim ${invite.puppy_name}` : "Sign in & claim"}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
